import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Inbox, Check, Phone, Mail, X, RefreshCw, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import api from "../lib/api";

const STATUSES = [
    { key: "new", label: "New", short: "New", dotClass: "bg-emerald-400", activeCls: "bg-emerald-500/15 border-emerald-700/40 text-emerald-200" },
    { key: "contacted", label: "Contacted", short: "Contacted", dotClass: "bg-amber-400", activeCls: "bg-amber-500/15 border-amber-700/40 text-amber-200" },
    { key: "booked", label: "Booked", short: "Booked", dotClass: "bg-sky-400", activeCls: "bg-sky-500/15 border-sky-700/40 text-sky-200" },
    { key: "completed", label: "Completed", short: "Done", dotClass: "bg-zinc-500", activeCls: "bg-zinc-700/50 border-zinc-600 text-zinc-100" },
];

function StatusPill({ status, size = "sm" }) {
    const s = STATUSES.find((x) => x.key === status) || STATUSES[0];
    const sizing = size === "lg" ? "text-[12.5px] px-2.5 py-1" : "text-[11.5px] px-2 py-0.5";
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-950/60 ${sizing} text-zinc-300`}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${s.dotClass}`} />
            {s.label}
        </span>
    );
}

/**
 * Inline one-tap status switcher. Designed to live on cards/rows so the contractor
 * never has to open a modal to move a lead through the pipeline.
 */
function LeadStatusToggle({ lead, onChange, busy, compact = false, testidPrefix = "lead-toggle" }) {
    return (
        <div
            role="radiogroup"
            aria-label="Lead status"
            data-testid={`${testidPrefix}-${lead.id}`}
            className={[
                "grid grid-cols-2 sm:inline-flex items-stretch rounded-lg border border-zinc-800 bg-zinc-950/70 p-0.5 overflow-hidden",
                compact ? "gap-0" : "gap-0",
                busy ? "opacity-70" : "",
            ].join(" ")}
            onClick={(e) => e.stopPropagation()}
        >
            {STATUSES.map((s) => {
                const isActive = lead.status === s.key;
                return (
                    <button
                        key={s.key}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        disabled={busy}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!isActive) onChange(lead, s.key);
                        }}
                        data-testid={`${testidPrefix}-${lead.id}-${s.key}`}
                        className={[
                            "relative inline-flex items-center justify-center gap-1.5 rounded-md transition-colors duration-150 select-none",
                            compact
                                ? "h-11 md:h-7 px-2 text-[12px] md:text-[11.5px] flex-1 min-w-0"
                                : "h-11 md:h-8 px-2.5 text-[12px] flex-1 sm:flex-none min-w-0",
                            isActive
                                ? `${s.activeCls} border shadow-[0_0_0_1px_rgba(255,255,255,0.04)_inset]`
                                : "border border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/70",
                            busy ? "cursor-wait" : "cursor-pointer",
                        ].join(" ")}
                    >
                        <span className={`h-1.5 w-1.5 rounded-full ${s.dotClass} shrink-0`} />
                        <span className="truncate">{compact ? s.short : s.label}</span>
                    </button>
                );
            })}
        </div>
    );
}

function relativeTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
}

function contactBits(contact) {
    if (!contact) return [];
    return contact.split("·").map((c) => c.trim()).filter(Boolean);
}

function contactIcon(bit) {
    if (/^\(?\d/.test(bit) || /\d{3}/.test(bit.replace(/\D/g, "").slice(0, 3))) return Phone;
    if (bit.includes("@")) return Mail;
    return Phone;
}

export default function Leads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);
    const [busyIds, setBusyIds] = useState(() => new Set());

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const { data } = await api.get("/leads");
            setLeads(data || []);
        } catch (_err) {
            setLoadError("We couldn't refresh your leads. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => {
        const c = { all: leads.length };
        STATUSES.forEach((s) => (c[s.key] = 0));
        leads.forEach((l) => {
            c[l.status] = (c[l.status] || 0) + 1;
        });
        return c;
    }, [leads]);

    const filtered = useMemo(
        () => (filter === "all" ? leads : leads.filter((l) => l.status === filter)),
        [leads, filter]
    );

    const handleStatusChange = async (lead, newStatus) => {
        if (newStatus === lead.status) return;
        setBusyIds((prev) => {
            const next = new Set(prev);
            next.add(lead.id);
            return next;
        });
        // Optimistic update — feels instant on touch
        const prevStatus = lead.status;
        setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: newStatus } : l)));
        try {
            const { data } = await api.patch(`/leads/${lead.id}`, { status: newStatus });
            setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
            setActive((curr) => (curr && curr.id === data.id ? data : curr));
            const nextLabel = STATUSES.find((s) => s.key === newStatus)?.label || newStatus;
            toast.success(`Marked as ${nextLabel}`, { duration: 1500 });
        } catch (_err) {
            // Roll back on failure
            setLeads((prev) =>
                prev.map((l) => (l.id === lead.id ? { ...l, status: prevStatus } : l))
            );
            toast.error("Could not update status");
        } finally {
            setBusyIds((prev) => {
                const next = new Set(prev);
                next.delete(lead.id);
                return next;
            });
        }
    };

    return (
        <section
            className="mx-auto w-full max-w-5xl px-5 sm:px-8 pt-6 sm:pt-16 pb-40 md:pb-16"
            data-testid="leads-page"
        >
            {/* Header */}
            <div className="flex items-end justify-between gap-4">
                <div>
                    <h1
                        className="text-3xl sm:text-4xl font-semibold tracking-tighter text-zinc-50"
                        data-testid="leads-heading"
                    >
                        Leads
                    </h1>
                    <p className="mt-2 text-[14.5px] text-zinc-500">
                        Every inbound lead from your site, in one place.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    aria-label="Refresh leads"
                    data-testid="leads-refresh-button"
                    disabled={loading}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors disabled:opacity-50"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {loadError && <div role="alert" data-testid="leads-load-error" className="portal-feedback mt-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm">{loadError}</p>
                <button type="button" data-testid="leads-retry-button" onClick={load} className="text-sm">Try again</button>
            </div>}

            {/* Filter chips */}
            <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-2" data-testid="leads-filter-bar">
                {[{ key: "all", label: "All" }, ...STATUSES].map((s) => {
                    const isActive = filter === s.key;
                    return (
                        <button
                            key={s.key}
                            type="button"
                            onClick={() => setFilter(s.key)}
                            data-testid={`leads-filter-${s.key}`}
                            className={[
                                "inline-flex items-center gap-2 h-8 px-3 rounded-full border text-[12.5px] transition-colors",
                                isActive
                                    ? "border-zinc-600 bg-zinc-900 text-zinc-50"
                                    : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700",
                            ].join(" ")}
                        >
                            {s.dotClass && <span className={`h-1.5 w-1.5 rounded-full ${s.dotClass}`} />}
                            {s.label}
                            <span className="text-zinc-500">{counts[s.key] ?? 0}</span>
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            <div className="mt-8" data-testid="leads-list">
                {loading && (
                    <div className="text-[13.5px] text-zinc-500 py-10">Loading leads…</div>
                )}

                {!loading && !loadError && filtered.length === 0 && (
                    <div
                        className="rounded-xl border border-dashed border-zinc-800 bg-transparent p-12 text-center"
                        data-testid="leads-empty"
                    >
                        <Inbox className="h-5 w-5 mx-auto text-zinc-600" />
                        <p className="mt-3 text-[14px] text-zinc-300">No leads to show.</p>
                        <p className="mt-1 text-[12.5px] text-zinc-500">
                            New leads from your website land here automatically.
                        </p>
                    </div>
                )}

                {/* Mobile cards */}
                {!loading && filtered.length > 0 && (
                    <ul className="md:hidden space-y-3" data-testid="leads-mobile-list">
                        {filtered.map((l) => (
                            <li key={l.id}>
                                <div
                                    data-testid={`lead-card-${l.id}`}
                                    className="rounded-xl border border-zinc-800 bg-transparent p-5 hover:border-zinc-700 transition-colors"
                                >
                                    <button
                                        type="button"
                                        onClick={() => setActive(l)}
                                        data-testid={`lead-card-open-${l.id}`}
                                        className="w-full text-left"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-[15px] font-medium text-zinc-50 truncate">
                                                    {l.name}
                                                </p>
                                                <p className="mt-0.5 text-[13px] text-zinc-400 truncate">
                                                    {l.service}
                                                </p>
                                            </div>
                                            {busyIds.has(l.id) && (
                                                <Loader2 className="h-3.5 w-3.5 text-zinc-500 animate-spin shrink-0 mt-1" />
                                            )}
                                        </div>
                                        <div className="mt-3 flex items-center justify-between text-[12px] text-zinc-500">
                                            <span className="truncate">{l.contact_info}</span>
                                            <span className="shrink-0 ml-3">{relativeTime(l.created_at)}</span>
                                        </div>
                                    </button>
                                    <div className="mt-4 grid">
                                        <LeadStatusToggle
                                            lead={l}
                                            onChange={handleStatusChange}
                                            busy={busyIds.has(l.id)}
                                            compact
                                            testidPrefix="lead-card-toggle"
                                        />
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {/* Desktop table */}
                {!loading && filtered.length > 0 && (
                    <div
                        className="hidden md:block rounded-xl border border-zinc-800 overflow-x-auto"
                        data-testid="leads-desktop-table"
                    >
                        <table className="w-full text-left table-fixed min-w-[820px]">
                            <colgroup>
                                <col className="w-[18%]" />
                                <col className="w-[18%]" />
                                <col className="w-[22%]" />
                                <col className="w-[30%]" />
                                <col className="w-[12%]" />
                            </colgroup>
                            <thead>
                                <tr className="text-[11.5px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-800">
                                    <th className="font-medium py-3 px-5">Name</th>
                                    <th className="font-medium py-3 px-5">Service</th>
                                    <th className="font-medium py-3 px-5">Contact</th>
                                    <th className="font-medium py-3 px-5">Status</th>
                                    <th className="font-medium py-3 px-5 text-right">Received</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((l) => (
                                    <tr
                                        key={l.id}
                                        onClick={() => setActive(l)}
                                        data-testid={`lead-row-${l.id}`}
                                        className="border-b border-zinc-900 last:border-0 cursor-pointer hover:bg-zinc-950/60 transition-colors"
                                    >
                                        <td className="py-4 px-5 text-[14px] text-zinc-100 truncate">{l.name}</td>
                                        <td className="py-4 px-5 text-[13.5px] text-zinc-300 truncate">{l.service}</td>
                                        <td className="py-4 px-5 text-[13px] text-zinc-400 truncate">{l.contact_info}</td>
                                        <td className="py-4 px-3">
                                            <LeadStatusToggle
                                                lead={l}
                                                onChange={handleStatusChange}
                                                busy={busyIds.has(l.id)}
                                                compact
                                                testidPrefix="lead-row-toggle"
                                            />
                                        </td>
                                        <td className="py-4 px-5 text-[12.5px] text-zinc-500 text-right whitespace-nowrap">
                                            {relativeTime(l.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <LeadDetailDrawer
                lead={active}
                onClose={() => setActive(null)}
                onChangeStatus={handleStatusChange}
                updating={active ? busyIds.has(active.id) : false}
            />

            <Toaster
                theme="dark"
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid #27272a",
                        color: "#f4f4f5",
                    },
                }}
            />
        </section>
    );
}

function LeadDetailDrawer({ lead, onClose, onChangeStatus, updating }) {
    return (
        <AnimatePresence>
            {lead && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="lead-drawer-overlay"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 30, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="fixed inset-x-0 bottom-0 sm:inset-y-0 sm:right-0 sm:left-auto sm:w-[440px] bg-[#0a0a0a] border-t sm:border-t-0 sm:border-l border-zinc-800 rounded-t-2xl sm:rounded-none flex flex-col max-h-[88vh] sm:max-h-none sm:h-full"
                        data-testid="lead-drawer"
                    >
                        <div className="flex items-start justify-between p-6 border-b border-zinc-800">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Lead</p>
                                <h2
                                    className="mt-1 text-xl font-medium text-zinc-50 tracking-tight truncate"
                                    data-testid="lead-drawer-name"
                                >
                                    {lead.name}
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="lead-drawer-close"
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Service</p>
                                <p className="mt-1.5 text-[14.5px] text-zinc-100">{lead.service}</p>
                            </div>

                            <div>
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Contact</p>
                                <ul className="mt-2 space-y-2">
                                    {contactBits(lead.contact_info).map((bit, idx) => {
                                        const Icon = contactIcon(bit);
                                        const isPhone = /^\(?\d/.test(bit);
                                        const isEmail = bit.includes("@");
                                        const href = isPhone
                                            ? `tel:${bit.replace(/[^\d+]/g, "")}`
                                            : isEmail
                                              ? `mailto:${bit}`
                                              : null;
                                        const Tag = href ? "a" : "div";
                                        return (
                                            <li key={idx}>
                                                <Tag
                                                    href={href || undefined}
                                                    className="flex items-center gap-2.5 text-[14px] text-zinc-200 hover:text-white transition-colors"
                                                    data-testid={`lead-drawer-contact-${idx}`}
                                                >
                                                    <Icon className="h-3.5 w-3.5 text-zinc-500" />
                                                    <span className="truncate">{bit}</span>
                                                </Tag>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            {lead.notes && (
                                <div>
                                    <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Notes</p>
                                    <p className="mt-1.5 text-[14px] text-zinc-300 leading-relaxed whitespace-pre-line">
                                        {lead.notes}
                                    </p>
                                </div>
                            )}

                            <div>
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Received</p>
                                <p className="mt-1.5 text-[13.5px] text-zinc-400">
                                    {new Date(lead.created_at).toLocaleString()}
                                </p>
                            </div>

                            <div>
                                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-3">
                                    Status
                                </p>
                                <div className="space-y-1.5" data-testid="lead-drawer-status-options">
                                    {STATUSES.map((s) => {
                                        const isActive = lead.status === s.key;
                                        return (
                                            <button
                                                key={s.key}
                                                type="button"
                                                disabled={updating}
                                                onClick={() => onChangeStatus(lead, s.key)}
                                                data-testid={`lead-drawer-status-${s.key}`}
                                                className={[
                                                    "w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border transition-colors text-left",
                                                    isActive
                                                        ? "border-zinc-700 bg-zinc-900 text-zinc-50"
                                                        : "border-zinc-800 hover:border-zinc-700 hover:bg-zinc-950 text-zinc-300",
                                                    updating ? "opacity-60 cursor-wait" : "",
                                                ].join(" ")}
                                            >
                                                <span className="flex items-center gap-2.5 text-[13.5px]">
                                                    <span className={`h-1.5 w-1.5 rounded-full ${s.dotClass}`} />
                                                    {s.label}
                                                </span>
                                                {isActive && <Check className="h-4 w-4 text-zinc-300" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
